import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { marked } from 'marked';
import DoFileUplaod from '../../../Form/Fields/DoFileUpload';
import fetchMethodRequest from '../../../../config/service';
import apiCalls from '../../../../config/apiCalls';
import showToasterMessage from '../../../UI/ToasterMessage/toasterMessage';
import DocumentPickerDrawer from './DocumentPickerDrawer';
import './CompareFiles.css';

/* ─── helpers ─────────────────────────────────────────────── */
const FILE_ICONS = {
  pdf: { icon: 'pi pi-file-pdf', color: '#ef4444' },
  docx: { icon: 'pi pi-file-word', color: '#2563eb' },
  doc: { icon: 'pi pi-file-word', color: '#2563eb' },
  xlsx: { icon: 'pi pi-file-excel', color: '#16a34a' },
  default: { icon: 'pi pi-file', color: '#64748b' },
};
function getIcon(name = '') {
  const ext = name.split('.').pop()?.toLowerCase();
  return FILE_ICONS[ext] || FILE_ICONS.default;
}
function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function bytesToSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function formatDiffHtml(html) {
  if (!html) return '';
  // difflib uses &nbsp; heavily which prevents natural text wrapping.
  // We replace them with normal spaces; 'white-space: pre-wrap' preserves them while allowing wrap.
  let modifiedHtml = html.replace(/&nbsp;/g, ' ');

  const customStyles = `
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background: #1e293b; 
        overflow: hidden !important;
      }
      .scroll-wrapper {
        height: 100vh;
        width: 100%;
        overflow: auto !important;
        padding: 16px;
        box-sizing: border-box;
        font-family: 'Inter', system-ui, sans-serif;
        color: #f1f5f9;
      }
      table.diff { 
        width: 100%; 
        border-collapse: collapse; 
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
        font-size: 13px;
        table-layout: auto !important;
      }
      table.diff td {
        padding: 4px 8px;
        border: 1px solid #334155;
        white-space: pre-wrap !important;
        word-wrap: break-word !important;
        max-width: 45vw;
      }
      table.diff th.diff_header, table.diff td.diff_header {
        width: 40px !important;
        min-width: 40px !important;
        text-align: right;
        background: #0f172a;
        color: #64748b;
        border-right: 2px solid #475569;
        user-select: none;
      }
      table.diff td.diff_next { 
        width: 20px !important; 
        min-width: 20px !important;
        background: #0f172a; 
        border-left: 2px solid #475569; 
        border-right: 2px solid #475569; 
      }
      table.diff .diff_add { background-color: rgba(34, 197, 94, 0.25); }
      table.diff .diff_chg { background-color: rgba(245, 158, 11, 0.25); }
      table.diff .diff_sub { background-color: rgba(239, 68, 68, 0.25); }
      
      table.diff[summary="Legends"] {
        width: auto;
        margin-top: 20px;
        table-layout: auto !important;
      }
      table.diff[summary="Legends"] td {
        max-width: none;
      }
    </style>
  `;
  if (modifiedHtml.includes('</head>')) {
    modifiedHtml = modifiedHtml.replace('</head>', customStyles + '</head>');
  } else {
    modifiedHtml = customStyles + modifiedHtml;
  }

  // Wrap the content inside body with the scrollable wrapper
  modifiedHtml = modifiedHtml.replace(/<body>/i, '<body><div class="scroll-wrapper">');
  modifiedHtml = modifiedHtml.replace(/<\/body>/i, '</div></body>');

  return modifiedHtml;
}

/* ─── Main component ──────────────────────────────────────── */
export const CompareFiles = () => {
  const navigate = useNavigate();

  /* file panel state */
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);

  /* upload refs for controlling DoFileUpload */
  const { control: ctrl1 } = useForm();
  const { control: ctrl2 } = useForm();

  /* picker drawer */
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null); // 1 or 2

  /* comparison */
  const [comparison, setComparison] = useState('');
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isComparing, setIsComparing] = useState(false);

  /* AI insights */
  const [insights, setInsights] = useState('');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsPanelOpen, setInsightsPanelOpen] = useState(false);

  /* recent sessions */
  const [recentSessions, setRecentSessions] = useState([]);

  const iframeRef = useRef(null);
  const resultRef = useRef(null);

  /* ── Load recent sessions on mount ── */
  useEffect(() => {
    fetchMethodRequest('GET', apiCalls.compareRecent)
      .then(res => { if (res?.chats) setRecentSessions(res.chats); })
      .catch(() => { });
  }, []);

  /* ── Scroll result into view after comparison ── */
  useEffect(() => {
    if (comparison && resultRef.current) {
      setTimeout(() => resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [comparison]);

  /* ─── Document panel upload success handler ─── */
  const handleUploadSuccess = (panelNum) => (resObj) => {
    if (!resObj) return;
    // DoFileUpload returns { [fieldName]: response } when type='comparefiles'
    // For DocumentPanel, fieldName is uploadFile1 or uploadFile2
    const actualRes = resObj.uploadFile1 || resObj.uploadFile2 || resObj.file1 || resObj.file2 || resObj;

    const fileData = {
      sourceType: 'upload',
      filePath: actualRes.filePath || actualRes.path || actualRes.fileName,
      fileName: actualRes.fileName || actualRes.name,
      name: actualRes.fileName || actualRes.name,
      type: (actualRes.fileName || actualRes.name || '').split('.').pop()?.toLowerCase(),
      size: actualRes.size,
    };
    if (panelNum === 1) setFile1(fileData);
    else setFile2(fileData);
  };

  /* ─── DMS picker selection ─── */
  const openPicker = (panelNum) => {
    setPickerTarget(panelNum);
    setPickerOpen(true);
  };

  const handlePickerSelect = (fileObj) => {
    if (pickerTarget === 1) setFile1(fileObj);
    else setFile2(fileObj);
    setPickerOpen(false);
  };

  /* ─── Clear a panel ─── */
  const clearPanel = (panelNum) => {
    if (panelNum === 1) setFile1(null);
    else setFile2(null);
    if (comparison) { setComparison(''); setInsights(''); setInsightsPanelOpen(false); }
  };

  /* ─── Run comparison ─── */
  const handleCompare = async () => {
    if (!file1 || !file2) {
      showToasterMessage('Please select or upload both documents.', 'error');
      return;
    }
    if (
      file1.sourceType === 'dms' && file2.sourceType === 'dms' &&
      file1.fileId === file2.fileId
    ) {
      showToasterMessage('Cannot compare a file with itself.', 'error');
      return;
    }

    setIsComparing(true);
    setComparison('');
    setInsights('');
    setInsightsPanelOpen(false);

    try {
      const body = {
        file1,
        file2,
        sessionName: `${file1.name} vs ${file2.name}`,
        ...(currentChatId ? { chatId: currentChatId } : {}),
      };

      const res = await fetchMethodRequest('POST', apiCalls.compareRun, body);

      if (res?.comparison) {
        setComparison(formatDiffHtml(res.comparison));
        setCurrentChatId(res.chatId?.toString());

        // Refresh recent sessions
        fetchMethodRequest('GET', apiCalls.compareRecent)
          .then(r => { if (r?.chats) setRecentSessions(r.chats); });

        // Kick off AI insights (non-blocking)
        fetchAIInsights(res.chatId?.toString(), file1.name, file2.name);
      } else {
        showToasterMessage('Comparison failed. Please try again.', 'error');
      }
    } catch (err) {
      showToasterMessage('An error occurred during comparison.', 'error');
    } finally {
      setIsComparing(false);
    }
  };

  /* ─── AI Insights ─── */
  const fetchAIInsights = async (chatId, f1Name, f2Name) => {
    if (!chatId) return;
    setInsightsLoading(true);
    setInsightsPanelOpen(true);
    try {
      const res = await fetchMethodRequest('POST', apiCalls.compareInsights, {
        chatId,
        file1Name: f1Name,
        file2Name: f2Name,
      });
      if (res?.insights) setInsights(res.insights);
    } catch (_) {
      setInsights('_AI insights unavailable for this comparison._');
    } finally {
      setInsightsLoading(false);
    }
  };

  /* ─── Restore recent session ─── */
  const restoreSession = async (chat) => {
    if (!chat?.answer) return;
    setComparison(formatDiffHtml(chat.answer));
    setCurrentChatId(chat._id);
    const [f1, f2] = (chat.files || []);
    if (f1) setFile1(f1);
    if (f2) setFile2(f2);
    setInsights('');

    // Load insights message if available
    fetchMethodRequest('GET', `${apiCalls.compareSession}/${chat._id}`)
      .then(res => {
        if (res?.insights?.answer) setInsights(res.insights.answer);
      }).catch(() => { });
  };

  /* ─── Delete session ─── */
  const deleteSession = async (e, chatId) => {
    e.stopPropagation();
    try {
      await fetchMethodRequest('DELETE', `${apiCalls.compareSession}/${chatId}`);
      setRecentSessions(prev => prev.filter(s => s._id !== chatId));
      if (currentChatId === chatId) {
        setComparison('');
        setCurrentChatId(null);
        setFile1(null);
        setFile2(null);
        setInsights('');
      }
    } catch (_) {
      showToasterMessage('Failed to delete session.', 'error');
    }
  };

  /* ─── Download report ─── */
  const downloadReport = () => {
    if (!comparison) return;
    const blob = new Blob([comparison], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparison-report-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ═══════════════ RENDER ═══════════════ */
  return (
    <div className="cw-page" style={{ maxHeight: 'calc(100vh - 100px)', overflowY: 'scroll' }}>
      {/* ── Top Toolbar ── */}
      <div className="cw-topbar">
        <div className="cw-topbar-left">
          <button className="cw-back-btn" onClick={() => navigate(-1)}>
            <i className="pi pi-arrow-left" />
          </button>
          <div className="cw-topbar-title">
            <div>
              <h1 className="cw-title">Compare Workspace</h1>
              <p className="cw-subtitle">Legal Document Comparison &amp; Analysis</p>
            </div>
          </div>
        </div>
        <div className="cw-topbar-actions">
          {comparison && (
            <>
              <button className="cw-btn cw-btn--ghost" onClick={downloadReport} title="Download HTML Report">
                <i className="pi pi-download" /> Download Report
              </button>
            </>
          )}
          <button
            className="cw-btn cw-btn--ghost"
            onClick={() => { setFile1(null); setFile2(null); setComparison(''); setInsights(''); setCurrentChatId(null); }}
            title="Clear workspace"
          >
            <i className="pi pi-refresh" /> New Session
          </button>
        </div>
      </div>


      {/* ── Document Panels ── */}
      <div className="cw-panels-row">
        <DocumentPanel
          panelNum={1}
          panelLabel="Document 1"
          file={file1}
          control={ctrl1}
          onUploadSuccess={handleUploadSuccess(1)}
          onPickFromDMS={() => openPicker(1)}
          onClear={() => clearPanel(1)}
        />

        <div className="cw-vs-divider">
          <div className="cw-vs-line" />
          <div className="cw-vs-badge">VS</div>
          <div className="cw-vs-line" />
        </div>

        <DocumentPanel
          panelNum={2}
          panelLabel="Document 2"
          file={file2}
          control={ctrl2}
          onUploadSuccess={handleUploadSuccess(2)}
          onPickFromDMS={() => openPicker(2)}
          onClear={() => clearPanel(2)}
        />
      </div>

      {/* ── Compare Action Bar ── */}
      <div className="cw-action-bar">
        <div className="cw-action-status">
          {file1 && file2 ? (
            <span className="cw-status-ready">
              <i className="pi pi-check-circle" /> Both documents ready — run comparison
            </span>
          ) : (
            <span className="cw-status-waiting">
              <i className="pi pi-info-circle" />
              {!file1 && !file2 ? 'Select or upload both documents' :
                !file1 ? 'Document 1 is missing' : 'Document 2 is missing'}
            </span>
          )}
        </div>
        <Button
          icon={isComparing ? 'pi pi-spin pi-spinner' : 'pi pi-copy'}
          label={isComparing ? 'Comparing...' : 'Compare Files'}
          className="cw-compare-btn"
          disabled={!file1 || !file2 || isComparing}
          onClick={handleCompare}
        />
      </div>

      {/* ── Comparison Result ── */}
      {comparison && (
        <div ref={resultRef} className="cw-result-section">
          {/* Result header */}
          <div className="cw-result-header">
            <div className="cw-result-header-left">
              <i className="pi pi-chart-bar cw-result-icon" />
              <div>
                <h2 className="cw-result-title">Comparison Results</h2>
                <p className="cw-result-subtitle">{file1?.name} vs {file2?.name}</p>
              </div>
            </div>
            <div className="cw-result-header-right">
              <div className="cw-legend">
                <span className="cw-legend-dot cw-legend-add" /> Additions
                <span className="cw-legend-dot cw-legend-del ms-3" /> Deletions
                <span className="cw-legend-dot cw-legend-chg ms-3" /> Changed
              </div>
              <button
                className="cw-btn cw-btn--ghost ms-3"
                onClick={() => setComparison('')}
                title="Clear results"
              >
                <i className="pi pi-times" />
              </button>
            </div>
          </div>

          {/* Result body: diff viewer + AI sidebar */}
          <div className="cw-result-body">
            {/* Diff viewer */}
            <div className={`cw-diff-viewer ${insightsPanelOpen ? 'cw-diff-viewer--narrow' : ''}`}>
              <iframe
                ref={iframeRef}
                srcDoc={comparison}
                className="cw-diff-frame"
                title="Document Comparison"
                sandbox="allow-same-origin"
                scrolling="yes"
              />
            </div>

            {/* AI Insights sidebar */}
            <div className={`cw-insights-sidebar ${insightsPanelOpen ? 'cw-insights-sidebar--open' : ''}`}>
              <button
                className="cw-insights-toggle"
                onClick={() => setInsightsPanelOpen(o => !o)}
                title="Toggle AI Insights"
              >
                <i className="pi pi-sparkles" />
                <span className="cw-insights-toggle-label">
                  {insightsPanelOpen ? 'Hide' : 'AI Insights'}
                </span>
                <i className={`pi pi-chevron-${insightsPanelOpen ? 'right' : 'left'} ms-1`} />
              </button>

              {insightsPanelOpen && (
                <div className="cw-insights-content">
                  <div className="cw-insights-header">
                    <i className="pi pi-sparkles cw-insights-icon" />
                    <h3 className="cw-insights-title">Legal AI Insights</h3>
                  </div>
                  <p className="cw-insights-desc">
                    Automated clause-level analysis for {file1?.name} vs {file2?.name}
                  </p>

                  {insightsLoading ? (
                    <div className="cw-insights-loading">
                      <i className="pi pi-spin pi-spinner cw-insights-spinner" />
                      <p>Analysing legal clauses...</p>
                      {['Payment Clauses', 'Termination Rights', 'Liability Terms', 'Confidentiality'].map(k => (
                        <div key={k} className="cw-insight-skeleton">
                          <div className="cw-skeleton-label">{k}</div>
                          <div className="cw-skeleton-bar" />
                          <div className="cw-skeleton-bar cw-skeleton-bar--short" />
                        </div>
                      ))}
                    </div>
                  ) : insights ? (
                    <div
                      className="cw-insights-body"
                      dangerouslySetInnerHTML={{ __html: marked(insights) }}
                    />
                  ) : (
                    <div className="cw-insights-empty">
                      <i className="pi pi-info-circle" /> No insights available.
                    </div>
                  )}

                  {/* Smart Suggestions */}
                  <div className="cw-suggestions">
                    <div className="cw-suggestions-title">Smart Suggestions</div>
                    <button className="cw-suggestion-chip" onClick={() => navigate('/privateAi')}>
                      <i className="pi pi-comments me-1" /> Discuss with AI
                    </button>
                    <button className="cw-suggestion-chip" onClick={downloadReport}>
                      <i className="pi pi-download me-1" /> Download Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Recent Sessions ── */}
      {recentSessions.length > 0 && (
        <div className="cw-recent-section">
          <div className="cw-recent-header">
            <i className="pi pi-history cw-recent-icon" />
            <h3 className="cw-recent-title">Recent Comparisons</h3>
          </div>
          <div className="cw-recent-grid">
            {recentSessions.map(session => {
              const [sf1, sf2] = session.files || [];
              return (
                <div
                  key={session._id}
                  className={`cw-recent-card ${currentChatId === session._id ? 'cw-recent-card--active' : ''}`}
                  onClick={() => restoreSession(session)}
                >
                  <div className="cw-recent-card-top">
                    <div className="cw-recent-files">
                      {sf1 && (
                        <span className="cw-recent-file">
                          <i className={getIcon(sf1.name).icon} style={{ color: getIcon(sf1.name).color }} />
                          {' '}{sf1.name}
                        </span>
                      )}
                      {sf2 && (
                        <>
                          <span className="cw-recent-vs">vs</span>
                          <span className="cw-recent-file">
                            <i className={getIcon(sf2.name).icon} style={{ color: getIcon(sf2.name).color }} />
                            {' '}{sf2.name}
                          </span>
                        </>
                      )}
                    </div>
                    <button
                      className="cw-recent-delete"
                      onClick={(e) => deleteSession(e, session._id)}
                      title="Delete session"
                    >
                      <i className="pi pi-trash" />
                    </button>
                  </div>
                  <div className="cw-recent-card-bottom">
                    <div className="cw-recent-badges">
                      {sf1 && (
                        <span className={`cw-source-badge cw-source-badge--${sf1.sourceType}`}>
                          {sf1.sourceType === 'dms' ? 'DMS' : 'Upload'}
                        </span>
                      )}
                      {sf2 && (
                        <span className={`cw-source-badge cw-source-badge--${sf2.sourceType}`}>
                          {sf2.sourceType === 'dms' ? 'DMS' : 'Upload'}
                        </span>
                      )}
                    </div>
                    <span className="cw-recent-date">{formatDate(session.created)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Document Picker Drawer ── */}
      <DocumentPickerDrawer
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        panelLabel={pickerTarget === 1 ? 'Document 1' : 'Document 2'}
      />
    </div>

  );
};

/* ════════════════════════════════════════════════
   DocumentPanel — sub-component
════════════════════════════════════════════════ */
const DocumentPanel = ({ panelNum, panelLabel, file, control, onUploadSuccess, onPickFromDMS, onClear }) => {
  const { icon, color } = file ? getIcon(file.name) : { icon: 'pi pi-file', color: '#94a3b8' };

  return (
    <div className="cw-panel">
      <div className="cw-panel-header">
        <div className="cw-panel-label">
          <span className="cw-panel-num">{panelNum}</span>
          {panelLabel}
        </div>
        {file && (
          <button className="cw-panel-clear" onClick={onClear} title="Remove document">
            <i className="pi pi-times" />
          </button>
        )}
      </div>

      <div className="cw-panel-body">
        {file ? (
          /* File info card */
          <div className="cw-file-card">
            <div className="cw-file-card-icon">
              <i className={icon} style={{ color, fontSize: '2.8rem' }} />
            </div>
            <div className="cw-file-card-info">
              <div className="cw-file-card-name" title={file.name}>{file.name}</div>
              <div className="cw-file-card-meta">
                {file.type && (
                  <span className="cw-type-badge">{file.type.toUpperCase()}</span>
                )}
                {file.sourceType === 'dms' ? (
                  <span className="cw-source-chip cw-source-chip--dms">
                    <i className="pi pi-database me-1" />My Documents
                  </span>
                ) : (
                  <span className="cw-source-chip cw-source-chip--upload">
                    <i className="pi pi-upload me-1" />Uploaded
                  </span>
                )}
              </div>
              {file.owner && (
                <div className="cw-file-card-owner">
                  <i className="pi pi-user me-1" />{file.owner}
                </div>
              )}
              {file.modified && (
                <div className="cw-file-card-date">
                  <i className="pi pi-calendar me-1" />{formatDate(file.modified)}
                </div>
              )}
              {file.size && (
                <div className="cw-file-card-size">{bytesToSize(file.size)}</div>
              )}
            </div>
            <button className="cw-file-replace-btn" onClick={onClear} title="Replace document">
              <i className="pi pi-refresh me-1" /> Replace
            </button>
          </div>
        ) : (
          /* Selection options */
          <div className="cw-selection-options">
            {/* Option A: Select from DMS */}
            <button className="cw-option-btn cw-option-btn--dms" onClick={onPickFromDMS}>
              <div className="cw-option-icon">
                <i className="pi pi-folder-open" />
              </div>
              <div className="cw-option-text">
                <div className="cw-option-title">Select from My Documents</div>
                <div className="cw-option-desc">Browse your DMS document library</div>
              </div>
              <i className="pi pi-chevron-right cw-option-arrow" />
            </button>

            <div className="cw-option-divider"><span>or</span></div>

            {/* Option B: Upload */}
            <div className="cw-upload-zone">
              <Controller
                name={`uploadFile${panelNum}`}
                control={control}
                render={({ field }) => (
                  <DoFileUplaod
                    field={field}
                    url="uploads?uploadPath=comparefiles"
                    type="comparefiles"
                    acceptType=".pdf,.docx,.doc,.txt"
                    showPreview={false}
                    userClickUpload={true}
                    maxFileSize={10000000}
                    isMultiple={false}
                    modeType="basic"
                    autoSubmit={true}
                    profile={true}
                    onSuccess={onUploadSuccess}
                  />
                )}
              />
              <div className="cw-upload-hint">
                <i className="pi pi-upload cw-upload-hint-icon" />
                <p>Drop a file here or click to browse</p>
                <p className="cw-upload-formats">PDF, DOCX, DOC, TXT • Max 10 MB</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};